var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// entities/adornisEntity.ts
import { A } from "@adornis/base/env-info.js";
import { logger } from "@adornis/base/logging.js";
import { Validator, validateFields } from "@adornis/validation/validator.js";
import { BehaviorSubject, combineLatest } from "rxjs";
import { distinctUntilChanged, filter, map } from "rxjs/operators";
import { EntityStore } from "../client/entityStore.js";
import { Field } from "../decorators/field.js";
import { baseqlMetaData } from "../metadata/metaDataStore.js";
import { _manualUpdate, fromNetwork, noChangeRecorded, upgradeMark } from "../symbols.js";
import { selectionSet } from "../utils/queryGeneration.js";
import { constructValue as constructValueX, toJSPrimitive } from "./construct.js";
import { defaultCompare, defaultMerge } from "./defaultFunctions.js";
var constructValue = constructValueX;
var _a;
var AdornisEntity = class {
  constructor(doc) {
    this.validator = new Validator({}, true);
    /** @deprecated this is going to be removed in a future release as it causes confusion about what "new" means */
    this.isNew = false;
    this[_a] = true;
    this._hasMetaObservable = false;
    this._metaPaused = false;
    this.isFromNetwork = doc[fromNetwork];
    const fields = Object.values(this.fields);
    this.doc = {};
    this.docMeta = {
      [_manualUpdate]: {
        omitted: false,
        state: new BehaviorSubject({ value: null, dirty: false })
      }
    };
    fields.forEach((field) => {
      if (doc[field.name] && typeof doc[field.name] === "object")
        doc[field.name][fromNetwork] = this.isFromNetwork;
      const value = constructValueX(
        field.options.default && !this.isFromNetwork ? field.options.default(doc[field.name]) : doc[field.name],
        field
      );
      const state = new BehaviorSubject({ value: noChangeRecorded, dirty: false });
      this.doc[field.name] = value;
      this.docMeta[field.name] = { state, omitted: value === void 0 };
      if (value === void 0 && !this.isFromNetwork)
        this.doc[field.name] = null;
    });
    this.doc._class = this.constructor._class;
    const propertyDescriptors = Object.fromEntries(
      fields.map((field) => [
        field.name,
        {
          get: () => {
            const value = this.getValue(field.name);
            if (this.isFromNetwork && this.getMetaData(field.name).omitted && value === void 0) {
              logger.error({ entity: this.doc._class, doc: this.doc, field: field.name }, "entity missing data");
              throw new Error(
                `Trying to access property ${field.name} on ${this.doc._class}, but it was not requested from remote`
              );
            }
            return value;
          },
          set: (value) => {
            const metaData = this.getMetaData(field.name);
            if (value && typeof value === "object" && !value[upgradeMark])
              value = constructValueX(value, field);
            const dirty = !(field.options.compare ?? defaultCompare)(this.doc[field.name], value, field);
            metaData.state.next({ value, dirty });
            if (this._hasMetaObservable) {
              this.redoFieldReactive(field);
            }
            return true;
          }
        }
      ])
    );
    Object.defineProperties(this, propertyDescriptors);
  }
  static {
    _a = upgradeMark;
  }
  static {
    /** minifier proof unique name */
    this._class = "AdornisEntity";
  }
  /** shortcut to get all metaFields corresponding to this entity */
  static get fields() {
    return this.prototype.fields;
  }
  /** @deprecated use `baseqlMetaData.entities.resolveNode` directly: EntityClass.metaTreeNode -> baseqlMetaData.entities.resolveNode(EntityClass) */
  static get metaTreeNode() {
    return baseqlMetaData.entities.resolveNode(this);
  }
  [validateFields]({
    validateOmitted,
    validateResolved = false
  }) {
    const fieldsToValidate = Object.keys(this.fields).filter((key) => {
      const metaData = this.getMetaData(key);
      const state = metaData.state.getValue();
      if (!validateResolved && this.fields[key]?.options.resolve)
        return false;
      return state.value !== noChangeRecorded || validateOmitted || !metaData.omitted;
    });
    return fieldsToValidate;
  }
  /** Resolves a unique ID out of this entity */
  static resolveID(entity) {
    throw new Error(`${this._class} does not implement resolveID`);
  }
  /**
   * getter for a graphQL selectionSet which selects all fields up to a certain depth.
   * By default it generates all fields up to a depth of 2.
   * You can use the selectionSet helper function from @adornis/baseql/utils/queryGeneration to go deeper.
   *
   * ```ts
   *   static get allFields() {
   *     return selectionSet(() => this, 2);
   *   }
   * ```
   *
   * @deprecated please use selectionSet(() => Type, 1) instead if you're looking for allFields. Don't use allFields with a depth > 1 blindly, it will kill performance down the line. Don't port blindly. This helper will be removed in the next major release.
   */
  static get allFields() {
    return selectionSet(() => this, 2);
  }
  static getByID(id) {
    throw new Error(`${this._class} does not implement getByID`);
  }
  makeFieldReactive(field, visited = /* @__PURE__ */ new Set()) {
    visited.add(this);
    const value = this.getValue(field.name);
    const metaData = this.docMeta[field.name];
    if (!metaData)
      throw new Error(`no metaData for field ${field.name} found. Something is very wrong.`);
    if (!metaData.metaSub && value?.[upgradeMark] && !visited.has(value)) {
      if (!metaData.metaObs)
        metaData.metaObs = value?.getMetaObservable?.(visited);
      metaData.metaSub = metaData.metaObs?.subscribe((valueMeta) => {
        const state = metaData.state.getValue();
        const newDirty = state.value !== noChangeRecorded || valueMeta.some((m) => m.dirty);
        metaData.state.next({ value: state.value, dirty: newDirty });
      });
    }
  }
  redoFieldReactive(field) {
    const metaData = this.docMeta[field.name];
    if (!metaData)
      throw new Error(`no metaData for field ${field.name} found. Something is very wrong.`);
    metaData.metaSub?.unsubscribe();
    delete metaData.metaSub;
    delete metaData.metaObs;
    this.makeFieldReactive(field);
  }
  getMetaObservable(visited = /* @__PURE__ */ new Set()) {
    this._hasMetaObservable = true;
    visited.add(this);
    Object.values(this.fields).forEach((field) => {
      this.makeFieldReactive(field, visited);
    });
    return combineLatest(Object.values(this.docMeta).map((x) => x.state)).pipe(filter((_) => !this._metaPaused));
  }
  /** Observable which fires this entity every time any of its values (including nested objects and arrays) changes. */
  get whenChanging() {
    return this.getMetaObservable().pipe(map(() => this));
  }
  getFieldsChanging(fieldList, visited = /* @__PURE__ */ new Set()) {
    Object.values(this.fields).filter((field) => fieldList.includes(field.name)).forEach((field) => {
      this.makeFieldReactive(field, visited);
    });
    return combineLatest(
      Object.entries(this.docMeta).filter(([key]) => fieldList.includes(key)).map(([_, value]) => value.state)
    ).pipe(filter((_) => !this._metaPaused)).pipe(map(() => this));
  }
  /** Observable which fires the dirty state of this entity. */
  get dirty() {
    return this.getMetaObservable().pipe(
      map((meta) => meta.some((m) => m.dirty)),
      distinctUntilChanged()
    );
  }
  getMetaData(fieldName) {
    const metaData = this.docMeta[fieldName];
    if (!metaData)
      throw new Error(`no metaData for field ${fieldName} found. Something is very wrong.`);
    return metaData;
  }
  getValue(fieldName) {
    const state = this.getMetaData(fieldName).state.getValue();
    if (state.dirty && state.value !== noChangeRecorded)
      return state.value;
    return this.doc[fieldName];
  }
  /**
   * If this entity contains valid information about the given field.
   *
   * In contrast to `if(entity.field)` `if(entity.has('field'))` can be used to check the presence of the field before actually accessing it
   * to prevent the AccessException if the field was not requested from remote.
   */
  has(fieldName) {
    return !this.isFromNetwork || !this.docMeta[fieldName]?.omitted;
  }
  /** describes whether the entity knows for sure about all its fields. This only applies for committed fields. */
  get isComplete() {
    return !Object.values(this.fields).reduce(
      (acc, field) => acc || this.getMetaData(field.name).omitted,
      false
    );
  }
  /** Validates the currently visible values via the in the validator stored in the validator property of this entity */
  async validate(options = {}) {
    await this.validator.validate(this, { aggregateErrors: true, recurse: true, ...options });
  }
  isDirty(recurse = true, visited = /* @__PURE__ */ new Set()) {
    visited.add(this);
    return Object.values(this.fields).some((field) => {
      const value = this.doc[field.name];
      const metaData = this.getMetaData(field.name);
      if (metaData.state.getValue().dirty)
        return true;
      if (value?.[upgradeMark] && !visited.has(value) && recurse)
        return value.isDirty(recurse, visited);
      return false;
    });
  }
  // TODO write integration tests under which circumstances things are commited, validated and saved
  // TODO take into account resolved fields, nested entities, arrays, etc
  /**
   * Recursively commits all dirty values to the `doc` dictionary
   *
   * set `force` to `true` to skip validation
   */
  async commit(force, visited = /* @__PURE__ */ new Set()) {
    await this._commit(force, visited);
    this.docMeta[_manualUpdate]?.state.next({ value: null, dirty: false });
  }
  async _commit(force, visited = /* @__PURE__ */ new Set()) {
    visited.add(this);
    this._metaPaused = true;
    if (!force)
      await this.validate({ validateOmitted: !this.isFromNetwork });
    const fields = Object.values(this.fields);
    for (const field of fields) {
      if (field.options.resolve)
        continue;
      const metaData = this.getMetaData(field.name);
      const state = metaData.state.getValue();
      if (state.value !== noChangeRecorded) {
        if (state.value?.[upgradeMark] && !visited.has(state.value))
          await state.value._commit(force, visited);
        this.doc[field.name] = state.value;
        metaData.state.next({ value: noChangeRecorded, dirty: false });
        metaData.omitted = this.doc[field.name] === void 0;
      } else if (this.doc[field.name]?.[upgradeMark] && !visited.has(this.doc[field.name])) {
        await this.doc[field.name]._commit(force, visited);
      }
    }
    this._metaPaused = false;
  }
  /** Recursively reverts any changes to the last commited state, resetting all values to the `doc` values */
  revert(visited = /* @__PURE__ */ new Set()) {
    this._revert(visited);
    this.docMeta[_manualUpdate]?.state.next({ value: null, dirty: false });
  }
  _revert(visited = /* @__PURE__ */ new Set()) {
    visited.add(this);
    this._metaPaused = true;
    Object.values(this.fields).forEach((field) => {
      const value = this.doc[field.name];
      const metaData = this.getMetaData(field.name);
      metaData.state.next({ value: noChangeRecorded, dirty: false });
      if (this._hasMetaObservable)
        this.redoFieldReactive(field);
      if (value?.[upgradeMark] && !visited.has(value))
        value._revert(visited);
    });
    this._metaPaused = false;
  }
  remove() {
    if (A.isClient)
      EntityStore.drop(this);
  }
  /**
   * Merge this entitiys doc with new `data`.
   *
   * Generally does not have to be called manually but is used by the `EntityStore` to update old entities with new data.
   *
   * This will recusrively update all (nested) values using the `merge` function of the field options if present.
   * By default the current value of the doc is overwritten if the new value is not undefined and `compare` returns false.
   * You can overwrite `merge` and `compare` using the field options for every field of the entity.
   *
   * @see EntityStore
   */
  mergeData(data, isFromNetwork) {
    if (data === void 0)
      return;
    if (data === null || !Object.keys(data).length) {
      console.warn("mergeData called without data!", this, data);
      return;
    }
    Object.values(this.fields).forEach((field) => {
      if (this.doc[field.name]?.[upgradeMark]) {
        if (field.options.merge) {
          const merged = field.options.merge(this.doc[field.name], data[field.name], field, this);
          const state = this.getMetaData(field.name).state.getValue();
          if (merged !== this.doc[field.name] && state.value === noChangeRecorded) {
            this.doc[field.name] = merged;
            if (this._hasMetaObservable)
              this.redoFieldReactive(field);
          }
        } else if (data[field.name] === null) {
          this.doc[field.name] = null;
          if (this._hasMetaObservable)
            this.redoFieldReactive(field);
        } else {
          this.doc[field.name].mergeData(data[field.name], isFromNetwork);
        }
      } else {
        if (data[field.name] && typeof data[field.name] === "object")
          data[field.name][fromNetwork] = isFromNetwork;
        const newValue = constructValueX(data[field.name], field);
        if (!(field.options.compare ?? defaultCompare)(this.doc[field.name], newValue, field)) {
          const metaData = this.getMetaData(field.name);
          this.doc[field.name] = (field.options.merge ?? defaultMerge)(
            this.doc[field.name],
            data[field.name],
            field,
            this,
            isFromNetwork
          );
          if (this.doc[field.name] !== void 0)
            metaData.omitted = false;
          metaData.state.next(metaData.state.getValue());
        }
      }
    });
  }
  /**
   * save logic inside AdornisEntity. This handles any operation neccessary to make the EntityStore work. Please __do not overwrite__ this.
   *
   * You should also call this if you overwrite the `save` method and do not want to (or cannot) call `super.save()`.
   *
   * Pass `true` to `force` to skip validation
   */
  async _save(force, ...args) {
    await this.commit(force);
    if (A.isClient)
      EntityStore.getAndMerge(this.toObject()) ?? EntityStore.put(this);
  }
  /**
   * save this entity. Implies `this.commit()`.
   *
   * If you overwrite this with additional saving logic (eG execute a mutation to save to database) please call `super.save()`.
   *
   * If you overwrite this further down the inheritance chain and you do not want to call super.save(),
   * please call this._save() instead.
   * This ensures all operations regarding the EntityStore are properly executed.
   */
  async save(...args) {
    return this._save();
  }
  /** create plain JS object out of this entity. used by `JSON.stringify()` to serialize. Filters resolved fields to not send them to the server on save */
  toJSON() {
    return this.toObject(true);
  }
  /** create plain JS object out of this entity. */
  toObject(filterResolved = false, visited = /* @__PURE__ */ new Set()) {
    visited.add(this);
    const me = Object.fromEntries(
      Object.values(this.fields).map((field) => {
        if (filterResolved && field.options.resolve && !field.options.includeInParameter)
          return [field.name, void 0];
        const state = this.getMetaData(field.name).state.getValue();
        const intermediate = state.dirty && state.value !== noChangeRecorded ? state.value : this.doc[field.name];
        const value = intermediate?.[upgradeMark] ? visited.has(intermediate) ? void 0 : intermediate.toObject(filterResolved, visited) : toJSPrimitive(intermediate, field.type);
        return [field.name, value];
      }).filter((pair) => pair[1] !== void 0)
    );
    visited.delete(this);
    return me;
  }
  /**
   * Returns all valid subclasses of this entity including its own class.
   *
   * can be used to restrict queries to only valid _class fields and selectively fetch subclasses
   * if multiple entities share a collection because they extend the same parent MongoEntity.
   *
   * @deprecated use `baseqlMetaData.entities.validSubclasses`
   */
  static validSubclasses(node) {
    return baseqlMetaData.entities.validSubclasses(node ?? this);
  }
};
__decorateClass([
  Field((type) => String)
], AdornisEntity.prototype, "_class", 2);
export {
  AdornisEntity,
  constructValue
};
